import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Package, QrCode, Check, Mail, Phone, MapPin, ChevronRight, ArrowLeft, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BoutiquePage = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, getToken } = useAuth();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [orders, setOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  // Charger les commandes depuis Supabase au montage
  useEffect(() => {
    if (currentUser) {
      fetchOrders();
    }
  }, [currentUser]);

  const fetchOrders = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${API}/boutique/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  // Pré-remplir le formulaire avec les infos du userProfile
  useEffect(() => {
    if (userProfile) {
      setFormData(prev => ({
        ...prev,
        businessName: userProfile.business_name || '',
        contactName: userProfile.owner_name || '',
        email: userProfile.email || currentUser?.email || '',
        phone: userProfile.phone || '',
      }));
    }
  }, [userProfile, currentUser]);

  const products = [
    {
      id: 'flyers',
      name: 'Flyers Premium',
      icon: <Package className="w-8 h-8" />,
      description: 'Flyers haute qualité pour promouvoir votre commerce avec QR code intégré',
      features: [
        'Format A5 (148x210mm)',
        'Papier couché 250g',
        'QR code personnalisé inclus',
        'Design professionnel',
        'Livraison sous 5-7 jours'
      ],
      prices: [
        { qty: 100, price: 49, pricePerUnit: 0.49 },
        { qty: 250, price: 99, pricePerUnit: 0.40, popular: true },
        { qty: 500, price: 169, pricePerUnit: 0.34 },
        { qty: 1000, price: 299, pricePerUnit: 0.30 }
      ],
      image: '📄'
    },
    {
      id: 'qrcode-stickers',
      name: 'Stickers QR Code',
      icon: <QrCode className="w-8 h-8" />,
      description: 'Stickers QR code résistants aux intempéries pour vitrine et comptoir',
      features: [
        'Format 10x10cm ou 15x15cm',
        'Vinyle résistant UV',
        'Adhésif permanent',
        'QR code haute résolution',
        'Livraison sous 3-5 jours'
      ],
      prices: [
        { qty: 10, price: 29, pricePerUnit: 2.90 },
        { qty: 25, price: 59, pricePerUnit: 2.36, popular: true },
        { qty: 50, price: 99, pricePerUnit: 1.98 },
        { qty: 100, price: 169, pricePerUnit: 1.69 }
      ],
      image: '🏷️'
    },
    {
      id: 'bundle',
      name: 'Pack Complet',
      icon: <ShoppingBag className="w-8 h-8" />,
      description: 'Combinaison flyers + stickers pour une visibilité maximale',
      features: [
        '250 flyers A5 premium',
        '25 stickers QR code 10x10cm',
        'QR codes personnalisés',
        'Design professionnel',
        'Économisez 20%'
      ],
      prices: [
        { qty: 1, price: 129, originalPrice: 158, pricePerUnit: 129, popular: true }
      ],
      image: '📦'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = await getToken();
      
      // Créer la commande via l'API
      const orderData = {
        product: selectedProduct.name,
        model: selectedProduct.name,
        quantity: quantity,
        price: selectedProduct.prices.find(p => p.qty === quantity)?.price || 0,
        customer_info: { ...formData }
      };
      
      const response = await axios.post(`${API}/boutique/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ajouter à la liste locale
      setOrders([response.data, ...orders]);
      
      toast.success('Commande enregistrée avec succès !');
      
      // Réinitialiser le formulaire en gardant les infos pré-remplies
      setSelectedProduct(null);
      setQuantity(1);
      setFormData({
        businessName: userProfile?.business_name || '',
        contactName: userProfile?.owner_name || '',
        email: userProfile?.email || currentUser?.email || '',
        phone: userProfile?.phone || '',
        address: '',
        notes: ''
      });
      
      // Scroller vers le haut pour voir la confirmation
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Erreur lors de la commande:', error);
      toast.error('Erreur lors de la commande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const token = await getToken();
      await axios.delete(`${API}/boutique/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOrders(orders.filter(o => o.id !== orderId));
      toast.success('Commande supprimée');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-white hover:text-cyan-300 hover:bg-white/10 transition-all flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour au Dashboard
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <ShoppingBag className="w-16 h-16 text-cyan-400 mx-auto" />
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Boutique Markeap
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Commandez vos supports marketing physiques pour booster la visibilité de votre commerce local
          </p>
        </motion.div>

        {/* Orders History */}
        {orders.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Package className="w-7 h-7 text-green-400" />
              Mes Commandes
            </h2>
            <div className="space-y-4">
              {orders.map((order) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="glass-card-white rounded-xl overflow-hidden border-2 border-green-200"
                >
                  {/* Order Header */}
                  <div className="p-6 bg-gradient-to-r from-green-50 to-cyan-50">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                          <h3 className="text-xl font-bold text-gray-900">{order.product}</h3>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <span>📅 {order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR') : 'N/A'}</span>
                          <span>📦 {order.quantity} unités</span>
                          <span className="font-semibold text-cyan-600">💶 {order.price}€</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-semibold">
                          {order.status}
                        </span>
                        <Button
                          onClick={() => handleDeleteOrder(order.id)}
                          variant="outline"
                          className="bg-white hover:bg-red-50 text-red-600 border-red-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                          variant="outline"
                          className="bg-white hover:bg-gray-50"
                        >
                          {expandedOrder === order.id ? (
                            <>
                              <ChevronUp className="w-4 h-4 mr-2" />
                              Masquer
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4 mr-2" />
                              Voir détails
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Order Details (Expanded) */}
                  {expandedOrder === order.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="p-6 bg-white border-t-2 border-gray-100"
                    >
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <Package className="w-5 h-5 text-cyan-600" />
                            Détails de la commande
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Produit :</span>
                              <span className="font-semibold text-gray-900">{order.product}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Modèle :</span>
                              <span className="font-semibold text-gray-900">{order.model}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Quantité :</span>
                              <span className="font-semibold text-gray-900">{order.quantity} unités</span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span className="text-gray-900 font-bold">Total :</span>
                              <span className="text-cyan-600 font-bold text-lg">{order.price}€</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <Mail className="w-5 h-5 text-cyan-600" />
                            Informations de livraison
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-600">Commerce :</span>
                              <p className="font-semibold text-gray-900">{order.customer_info?.businessName}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Contact :</span>
                              <p className="font-semibold text-gray-900">{order.customer_info?.contactName}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Email :</span>
                              <p className="font-semibold text-gray-900">{order.customer_info?.email}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Téléphone :</span>
                              <p className="font-semibold text-gray-900">{order.customer_info?.phone}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Adresse :</span>
                              <p className="font-semibold text-gray-900">{order.customer_info?.address}</p>
                            </div>
                            {order.customer_info?.notes && (
                              <div>
                                <span className="text-gray-600">Instructions :</span>
                                <p className="text-gray-900 italic">{order.customer_info.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>📧 Confirmation envoyée</strong> à {order.customer_info.email}. 
                          Notre équipe va traiter votre commande dans les plus brefs délais.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Products Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onClick={() => setSelectedProduct(product)}
              className={`glass-card p-6 rounded-2xl border-2 cursor-pointer transition-all hover:scale-105 ${
                selectedProduct?.id === product.id
                  ? 'border-cyan-500 shadow-lg shadow-cyan-500/50'
                  : 'border-white/10 hover:border-white/30'
              }`}
            >
              <div className="text-6xl mb-4 text-center">{product.image}</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  {product.icon}
                </div>
                <h3 className="text-xl font-bold text-white">{product.name}</h3>
              </div>
              <p className="text-gray-400 mb-4 text-sm">{product.description}</p>
              
              <div className="space-y-2 mb-4">
                {product.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4">
                <p className="text-sm text-gray-400 mb-2">À partir de</p>
                <p className="text-3xl font-bold text-cyan-400">
                  {product.prices[0].price}€
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Order Form */}
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="glass-card-white p-8 rounded-2xl max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <ChevronRight className="w-8 h-8 text-cyan-600" />
              Commander : {selectedProduct.name}
            </h2>

            {/* Quantity Selection */}
            {selectedProduct && (
              <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Choisissez votre quantité</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {selectedProduct.prices.map((priceOption) => (
                  <motion.button
                    key={priceOption.qty}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setQuantity(priceOption.qty)}
                    className={`relative p-4 rounded-xl border-2 transition-all ${
                      quantity === priceOption.qty
                        ? 'border-cyan-600 bg-cyan-50'
                        : 'border-gray-200 hover:border-cyan-300'
                    }`}
                  >
                    {priceOption.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        POPULAIRE
                      </div>
                    )}
                    <div className="text-2xl font-bold text-gray-900">{priceOption.qty}</div>
                    <div className="text-sm text-gray-600 mb-2">unités</div>
                    <div className="text-xl font-bold text-cyan-600">{priceOption.price}€</div>
                    {priceOption.originalPrice && (
                      <div className="text-sm text-gray-500 line-through">{priceOption.originalPrice}€</div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      {priceOption.pricePerUnit.toFixed(2)}€/unité
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
            )}

            {/* Contact Form */}
            {selectedProduct && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Vos informations</h3>
                {(formData.businessName || formData.email || formData.phone) && (
                  <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-green-800 font-medium">Infos pré-remplies</span>
                  </div>
                )}
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Nom du commerce *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.businessName}
                    onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                      formData.businessName ? 'border-green-300 bg-green-50/30' : 'border-gray-300'
                    }`}
                    placeholder="Restaurant Le Majestic"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Nom du contact *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.contactName}
                    onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                      formData.contactName ? 'border-green-300 bg-green-50/30' : 'border-gray-300'
                    }`}
                    placeholder="Jean Dupont"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                      formData.email ? 'border-green-300 bg-green-50/30' : 'border-gray-300'
                    }`}
                    placeholder="contact@restaurant.fr"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                      formData.phone ? 'border-green-300 bg-green-50/30' : 'border-gray-300'
                    }`}
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Adresse de livraison *
                </label>
                <textarea
                  required
                  rows="2"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="123 rue de la République, 75001 Paris"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Instructions spéciales (optionnel)
                </label>
                <textarea
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Mentions particulières pour le design, délai urgent, etc."
                />
              </div>

              {/* Order Summary */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-cyan-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Récapitulatif de commande</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Produit :</span>
                    <span className="font-semibold text-gray-900">{selectedProduct.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Quantité :</span>
                    <span className="font-semibold text-gray-900">{quantity} unités</span>
                  </div>
                  <div className="border-t border-cyan-200 pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total :</span>
                      <span className="text-3xl font-bold text-cyan-600">
                        {selectedProduct.prices.find(p => p.qty === quantity)?.price}€
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  * Prix TTC, livraison incluse en France métropolitaine
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-bold py-4 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all"
              >
                Confirmer la commande
              </Button>
            </form>
            )}
          </motion.div>
        )}

        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 grid md:grid-cols-3 gap-6"
        >
          <div className="glass-card p-6 rounded-xl border border-white/10 text-center">
            <Package className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Livraison Rapide</h3>
            <p className="text-gray-400 text-sm">Recevez votre commande sous 3 à 7 jours ouvrés</p>
          </div>
          
          <div className="glass-card p-6 rounded-xl border border-white/10 text-center">
            <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Qualité Premium</h3>
            <p className="text-gray-400 text-sm">Matériaux de haute qualité et impression professionnelle</p>
          </div>
          
          <div className="glass-card p-6 rounded-xl border border-white/10 text-center">
            <Mail className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Support Dédié</h3>
            <p className="text-gray-400 text-sm">Notre équipe vous accompagne dans votre commande</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BoutiquePage;
